/**
 * Marketplace Operations (WALLIYO-0132).
 *
 * `jobs`, `job_quotes`, `workers`, `worker_skills` and `reviews` have been
 * filling up since the app shipped with no screen anywhere. These four read
 * them.
 *
 * Nothing here writes. A job goes from requested to quoted to accepted because
 * a pro bid and a client chose; what operations can do is see where that is
 * not happening and go and ring somebody. So the Overview leads with the one
 * figure that is a person's job — the jobs nobody bid on — and everything else
 * on the page is context for it.
 */

import type {
  CategoryCoverage,
  JobListItem,
  MarketplaceOverviewResponse,
  MarketplaceReview,
  ProviderListItem,
} from "@wallyo/contracts/console/marketplace";
import { useState } from "react";

import {
  getMarketplaceOverview,
  listMarketplaceCategories,
  listMarketplaceJobs,
  listMarketplaceProviders,
  listMarketplaceReviews,
} from "../../api/console-api";
import { useApi } from "../../api/context";
import { CATEGORIES, type CategoryKey } from "@wallyo/contracts/categories";

import { t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { PageTop } from "../../shell/PageTop";
import { Head } from "../overview/OverviewScreen";
import { LoadFailed, Loading, useLoad } from "../config-parts";

const NO_CITY = "—";

/**
 * A trade's name, from the catalogue the app itself uses.
 *
 * Not `categoryName`, which names a *settings* category. These are the
 * twenty-one trades in `shared/src/categories.ts`, and they are the same
 * words a customer picks from in the app — so they are not translated here
 * and then drift from what the pro was shown.
 */
const tradeName = (key: CategoryKey): string => CATEGORIES[key]?.label ?? key;

/** A share as a bar, or nothing to say when nobody asked. */
function Coverage({ share }: { share: number | null }) {
  if (share === null) return <span className="muted-cell">{t("marketplace.noDemand")}</span>;
  const percent = Math.round(share * 100);
  const tone = percent >= 75 ? "var(--bar)" : percent >= 40 ? "var(--warn)" : "var(--bad)";
  return (
    <span className="mkt-bar" title={`${percent}%`}>
      <span className="mkt-bar-fill" style={{ width: `${percent}%`, background: tone }} />
    </span>
  );
}

// ── Overview ─────────────────────────────────────────────────────────────

export function MarketplaceOverview() {
  const { client } = useApi();
  const load = useLoad<MarketplaceOverviewResponse>(() => getMarketplaceOverview(client), "marketplace");
  const data = load.state.data;

  return (
    <>
      {data ? <Head head={data.head} /> : <PageTop title={t("marketplace.title")} />}
      {load.state.status === "failed" && !data ? (
        <LoadFailed
          message={t("marketplace.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {!data && load.state.status === "loading" ? <Loading text={t("hr.loading")} /> : null}
      {data ? (
        <>
          {/* The funnel. The last figure is the only one that is somebody's job. */}
          <div className="mkt-figures">
            <Figure n={data.funnel.posted} caption={t("marketplace.posted")} />
            <Figure n={data.funnel.quoted} caption={t("marketplace.gotQuote")} />
            <Figure n={data.funnel.accepted} caption={t("marketplace.accepted")} tone="ok" />
            <Figure n={data.funnel.noQuotes} caption={t("marketplace.noBids")} tone="bad" />
            <Figure
              label={
                data.funnel.minutesToFirstQuote === null
                  ? "—"
                  : t("marketplace.hoursShort", {
                      hours: String(Math.round(data.funnel.minutesToFirstQuote / 60)),
                    })
              }
              caption={t("marketplace.toFirstQuote")}
            />
          </div>

          <div className="split list">
            <section className="card">
              <div className="ov-card-head">
                <h2>{t("marketplace.nobodyBid")}</h2>
                <span className="foot">{t("marketplace.nobodyBidHint")}</span>
                {data.funnel.noQuotes > data.unanswered.length ? (
                  <a className="go-link mkt-see-all" href="#jobs">
                    {t("marketplace.seeAll", { count: String(data.funnel.noQuotes) })}
                  </a>
                ) : null}
              </div>
              {data.unanswered.length === 0 ? (
                <div className="empty">{t("marketplace.allAnswered")}</div>
              ) : (
                <ul className="hr-plain">
                  {data.unanswered.map((job) => (
                    <li key={job.jobId} className="ov-row">
                      <span className="grow">
                        <span className="ov-row-what">
                          {tradeName(job.category)} · {job.city ?? NO_CITY}
                        </span>
                        <span className={job.prosNearby === 0 ? "ov-row-why danger" : "ov-row-why"}>
                          {t("marketplace.waitingDays", { days: String(job.waitingDays) })}
                          {" · "}
                          {job.prosNearby === 0
                            ? t("marketplace.noProHere")
                            : t("marketplace.prosNearby", { count: String(job.prosNearby) })}
                        </span>
                      </span>
                      {/* Nobody in the console can make a pro bid. What they can
                          do is go and find one, which happens on the phone. */}
                      <a className={job.prosNearby === 0 ? "btn primary" : "btn"} href="#providers">
                        {job.prosNearby === 0 ? t("marketplace.findAPro") : t("overview.action.open")}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="card">
              <h2>{t("marketplace.demandNoSupply")}</h2>
              <p className="foot">{t("marketplace.demandNoSupplyHint")}</p>
              {data.gaps.length === 0 ? (
                <div className="empty">{t("marketplace.everywhereCovered")}</div>
              ) : (
                <ul className="hr-plain">
                  {data.gaps.map((gap) => (
                    <li key={`${gap.category}:${gap.city ?? ""}`} className="role-holder">
                      <strong>
                        {tradeName(gap.category)} · {gap.city ?? NO_CITY}
                      </strong>
                      <div className="grow" />
                      <span className="mono danger">{gap.jobs}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="foot">{t("marketplace.activePros", { count: String(data.activePros) })}</p>
            </section>
          </div>
        </>
      ) : null}
    </>
  );
}

function Figure({ n, label, caption, tone }: { n?: number; label?: string; caption: string; tone?: "ok" | "bad" }) {
  return (
    <div className={tone ? `mkt-figure ${tone}` : "mkt-figure"}>
      <span className="figure">{label ?? n}</span>
      <span className="foot">{caption}</span>
    </div>
  );
}

// ── Jobs ─────────────────────────────────────────────────────────────────

const JOB_FILTERS = ["all", "no_quotes", "requested", "quoted", "accepted", "cancelled"] as const;
type JobFilter = (typeof JOB_FILTERS)[number];

export function MarketplaceJobs() {
  const { client } = useApi();
  // Opens on the gap, because that is the only row here that needs a person.
  const [filter, setFilter] = useState<JobFilter>("no_quotes");
  const load = useLoad<{ jobs: readonly JobListItem[] }>(
    () => listMarketplaceJobs(client, filter === "all" ? {} : { status: filter }),
    filter,
  );
  const jobs = load.state.data?.jobs ?? [];

  return (
    <>
      <PageTop title={t("marketplace.jobs")} sub={t("marketplace.jobsSub")} />
      <div className="hr-search">
        {JOB_FILTERS.map((each) => (
          <button
            key={each}
            type="button"
            className={each === filter ? "btn small" : "btn quiet small"}
            onClick={() => setFilter(each)}
          >
            {t(`marketplace.filter.${each}` as never)}
          </button>
        ))}
      </div>
      {load.state.status === "failed" && !load.state.data ? (
        <LoadFailed
          message={t("marketplace.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {!load.state.data && load.state.status === "loading" ? <Loading text={t("hr.loading")} /> : null}
      {load.state.data ? (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("marketplace.col.job")}</th>
                  <th>{t("marketplace.col.city")}</th>
                  <th>{t("marketplace.col.posted")}</th>
                  <th className="num">{t("marketplace.col.quotes")}</th>
                  <th className="num">{t("marketplace.col.estimate")}</th>
                  <th>{t("marketplace.col.status")}</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.jobId}>
                    <td>
                      <strong>{tradeName(job.category)}</strong>
                      <div className="foot mono">{job.reference}</div>
                    </td>
                    <td className="foot">{job.city ?? NO_CITY}</td>
                    <td className="foot">{formatDateTime(job.postedAt)}</td>
                    <td className={job.quotes === 0 ? "num danger" : "num mono"}>{job.quotes}</td>
                    <td className="num mono">{job.priceEstimate === null ? "—" : job.priceEstimate}</td>
                    <td>
                      <span className={`pill ${job.quotes === 0 ? "bad" : job.status === "accepted" ? "ok" : ""}`}>
                        {t(`marketplace.status.${job.status}` as never)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {jobs.length === 0 ? <div className="empty">{t("marketplace.noJobs")}</div> : null}
        </section>
      ) : null}
    </>
  );
}

// ── Providers ────────────────────────────────────────────────────────────

export function MarketplaceProviders() {
  const { client } = useApi();
  const [neverQuoted, setNeverQuoted] = useState(false);
  const load = useLoad<{ providers: readonly ProviderListItem[] }>(
    () => listMarketplaceProviders(client, neverQuoted ? { neverQuoted: true } : {}),
    String(neverQuoted),
  );
  const providers = load.state.data?.providers ?? [];

  return (
    <>
      <PageTop title={t("marketplace.providers")} sub={t("marketplace.providersSub")} />
      <div className="hr-search">
        <button
          type="button"
          className={neverQuoted ? "btn small" : "btn quiet small"}
          onClick={() => setNeverQuoted(true)}
        >
          {t("marketplace.neverQuoted")}
        </button>
        <button
          type="button"
          className={neverQuoted ? "btn quiet small" : "btn small"}
          onClick={() => setNeverQuoted(false)}
        >
          {t("marketplace.allPros")}
        </button>
      </div>
      {load.state.status === "failed" && !load.state.data ? (
        <LoadFailed
          message={t("marketplace.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {load.state.data ? (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("marketplace.col.pro")}</th>
                  <th>{t("marketplace.col.city")}</th>
                  <th>{t("marketplace.col.skills")}</th>
                  <th className="num">{t("marketplace.col.quotesGiven")}</th>
                  <th className="num">{t("marketplace.col.rating")}</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((pro) => (
                  <tr key={pro.workerId}>
                    <td>
                      <strong>{pro.name}</strong>
                      {pro.verified ? null : (
                        <>
                          {" "}
                          <span className="pill warn">{t("marketplace.unverified")}</span>
                        </>
                      )}
                    </td>
                    <td className="foot">{pro.city ?? NO_CITY}</td>
                    <td className="foot">{pro.skills.map(tradeName).join(", ") || "—"}</td>
                    <td className={pro.quotesGiven === 0 ? "num danger" : "num mono"}>{pro.quotesGiven}</td>
                    <td className="num mono">{pro.rating === null ? "—" : pro.rating.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {providers.length === 0 ? <div className="empty">{t("marketplace.noPros")}</div> : null}
        </section>
      ) : null}
    </>
  );
}

// ── Categories ───────────────────────────────────────────────────────────

export function MarketplaceCategories() {
  const { client } = useApi();
  const load = useLoad<{ categories: readonly CategoryCoverage[] }>(
    () => listMarketplaceCategories(client),
    "categories",
  );
  const rows = load.state.data?.categories ?? [];

  return (
    <>
      <PageTop title={t("marketplace.categories")} sub={t("marketplace.categoriesSub")} />
      {load.state.status === "failed" && !load.state.data ? (
        <LoadFailed
          message={t("marketplace.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {load.state.data ? (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("marketplace.col.category")}</th>
                  <th className="num">{t("marketplace.col.jobs")}</th>
                  <th className="num">{t("marketplace.col.pros")}</th>
                  <th>{t("marketplace.col.coverage")}</th>
                  <th>{t("marketplace.col.uncovered")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.category}>
                    <td>
                      <strong>{tradeName(row.category)}</strong>
                    </td>
                    <td className="num mono">{row.jobs}</td>
                    <td className={row.pros === 0 ? "num danger" : "num mono"}>{row.pros}</td>
                    <td>
                      <Coverage share={row.coverage} />
                    </td>
                    {/* A recruiting list, not a report. */}
                    <td className={row.citiesUncovered.length > 0 ? "danger" : "foot"}>
                      {row.citiesUncovered.join(", ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length === 0 ? <div className="empty">{t("marketplace.noCategories")}</div> : null}
          <p className="foot">{t("marketplace.coverageHint")}</p>
        </section>
      ) : null}
    </>
  );
}

// ── Reviews ──────────────────────────────────────────────────────────────

export function MarketplaceReviews() {
  const { client } = useApi();
  const [poorOnly, setPoorOnly] = useState(true);
  const load = useLoad<{ reviews: readonly MarketplaceReview[]; average: number | null; total: number }>(
    () => listMarketplaceReviews(client, poorOnly ? { atMost: 3 } : {}),
    String(poorOnly),
  );
  const data = load.state.data;

  return (
    <>
      <PageTop title={t("marketplace.reviews")} sub={t("marketplace.reviewsSub")} />
      <div className="hr-search">
        <button type="button" className={poorOnly ? "btn small" : "btn quiet small"} onClick={() => setPoorOnly(true)}>
          {t("marketplace.poorOnly")}
        </button>
        <button type="button" className={poorOnly ? "btn quiet small" : "btn small"} onClick={() => setPoorOnly(false)}>
          {t("marketplace.allReviews")}
        </button>
        <div className="grow" />
        {data ? (
          <span className="foot">
            {t("marketplace.averageIs", {
              average: data.average === null ? "—" : data.average.toFixed(2),
              total: String(data.total),
            })}
          </span>
        ) : null}
      </div>
      {load.state.status === "failed" && !data ? (
        <LoadFailed
          message={t("marketplace.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {data ? (
        <section className="card">
          {data.reviews.length === 0 ? (
            <div className="empty">{t("marketplace.noReviews")}</div>
          ) : (
            <ul className="hr-plain">
              {data.reviews.map((review) => (
                <li key={review.reviewId} className="role-holder">
                  <span className={review.rating <= 2 ? "pill bad" : review.rating <= 3 ? "pill warn" : "pill"}>
                    {review.rating}/5
                  </span>
                  <strong>{review.providerName}</strong>
                  <span className="foot">{tradeName(review.category)}</span>
                  <div className="grow" />
                  {review.comment ? <bdi className="foot">{review.comment}</bdi> : null}
                  <span className="foot">{formatDateTime(review.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </>
  );
}
